import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { isNil, isUndefined } from 'lodash';
import { AppState } from 'src/store';
import { ErrorBoundaryComponentError } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import styles from 'src/components/ErrorBoundary/ErrorBoundary.styles';
import { classes } from 'typestyle';
import service from 'src/ServiceContainer';
import { Accordion, AccordionSummary, Typography, AccordionDetails } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { zodErrorToHumanMessage } from './Erroboundary.utils';
import { AxiosError, isAxiosError } from 'axios';
import ServiceContainer from 'src/ServiceContainer';
import { ZodError } from 'zod';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
interface ErrorBoundaryProps {
  error: ErrorBoundaryComponentError | null;
}

export const ErrorBoundary = (props: ErrorBoundaryProps) => {
  const { error } = props;
  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (error) {
      const message = `A ${error.type} error occured while loading ${error.name}. Details: ${error.message}`;
      service.loggingService.error(message);
      if (error.stack) {
        ServiceContainer.loggingService.error(`An error occurred: ${error.stack}`);
      }
      if (error.issues) {
        const errorIssues = error.issues;
        ServiceContainer.loggingService.error(`An error occurred: ${errorIssues}`);
      }
    }
  }, [error]);

  if (isNil(error)) {
    // @ts-ignore
    return props.children;
  }

  // Determine if this is a server error to adjust UI text
  const isServerRequestError = isAxiosError(error.issues);

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper({ wrapperType: 'error' })}>
        <section className={styles.textWrapper}>
          <div className={classes(styles.textSpacer, styles.textHeader, 'warning')}>
            {props.error
              ? `An application ${props.error.type} error has occurred`
              : `An error occured in the application `}
          </div>
          <div className={classes(styles.textSpacer, styles.textItem)}>
            You may attempt to reload the application and try again.
          </div>
          <div className={styles.textItem}> If the error persists, please contact support.</div>
        </section>
        <section className={styles.errorDropdown}>
          {error.issues ? (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>
                  {isServerRequestError ? 'Server Request Failed' : 'Invalid Configuration Items'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(() => {
                  if (error.issues instanceof ZodError) {
                    return (
                      <div>
                        <p>
                          {!isUndefined(error.defnId)
                            ? `Invalid config found at ${error.defnId} defn:`
                            : 'Invalid config found at:'}
                        </p>
                        <ul className="error-message-list">
                          {zodErrorToHumanMessage(error.issues as ZodError).map((err, idx) => {
                            return <li key={idx}>{err}</li>;
                          })}
                        </ul>
                      </div>
                    );
                  } else if (isServerRequestError) {
                    const axiosErr = error.issues as AxiosError;
                    const status = axiosErr?.response?.status;
                    return (
                      <ul className="error-message-list">
                        <li>An error occurred fetching data from the server.</li>
                        <li>HTTP Status: {status ?? 'Unknown'}</li>
                      </ul>
                    );
                  } else if (error.message.includes('invalid JSON')) {
                    return (
                      <div>
                        <p>{error.message}</p>
                      </div>
                    );
                  }
                  return (
                    <ul className="error-message-list">
                      <li>An unclassified error occured</li>
                    </ul>
                  );
                })()}
              </AccordionDetails>
            </Accordion>
          ) : null}
        </section>
        <SquareActionButton
          text="Reload"
          icon="fas fa-sync-alt"
          className={styles.buttonText}
          onClick={handleReload}
        />
      </div>
    </div>
  );
};

function mapStateToProps(state: AppState) {
  const currentError = state.error.currentError;
  return {
    error: currentError,
  };
}

export default connect(mapStateToProps)(ErrorBoundary);
